import {Component, Inject, OnInit}                                            from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef}                                        from '@angular/material/dialog';
import {MatSnackBar}                                                           from '@angular/material/snack-bar';
import {PermissionsService}                                                    from '@src/app/core/services/permissions.service';
import {DocumentParentType, OperationalDocument, operationalDocumentCategories} from '@src/app/shared/models/document.model';
import {DocumentsService}                                                      from '@src/app/shared/services/documents.service';
import {firstValueFrom}                                                        from 'rxjs';

export interface DocumentUploadDialogData
{
    documentableType: DocumentParentType;
    documentableId: string | number;
    document?: OperationalDocument;
}

@Component({
    standalone: false,
    selector: 'app-document-upload-dialog',
    templateUrl: './document-upload-dialog.component.html',
    styleUrls: ['./document-upload-dialog.component.scss'],
})
export class DocumentUploadDialogComponent implements OnInit
{
    private static readonly maxUploadBytes = 25 * 1024 * 1024;

    public readonly categories = operationalDocumentCategories;
    public selectedCategory = 'RAMS';
    public customCategory = '';
    public title = '';
    public selectedFile: File | null = null;
    public uploading = false;
    public visibleToCustomer = false;
    public visibleOnApp = true;
    public includeInReport = false;
    public readonly canManageVisibility: boolean;

    public constructor(
        @Inject(MAT_DIALOG_DATA) public data: DocumentUploadDialogData,
        public dialogRef: MatDialogRef<DocumentUploadDialogComponent, OperationalDocument>,
        private documentsService: DocumentsService,
        private snackBar: MatSnackBar,
        private permissionsService: PermissionsService,
    )
    {
        this.canManageVisibility = this.permissionsService.can('manage_document_visibility');
    }

    public ngOnInit(): void
    {
        const document = this.data.document;
        if (!document) {
            return;
        }

        this.title = document.title ?? '';
        this.visibleToCustomer = document.visible_to_customer;
        this.visibleOnApp = document.visible_on_app;
        this.includeInReport = document.include_in_report;

        if (document.category && this.categories.includes(document.category as typeof this.categories[number])) {
            this.selectedCategory = document.category;
        } else {
            this.selectedCategory = 'Other';
            this.customCategory = document.category ?? '';
        }
    }

    public selectFiles(files: ArrayLike<File> | null): void
    {
        const file = files?.[0] ?? null;
        if (!file) {
            return;
        }

        if (file.size > DocumentUploadDialogComponent.maxUploadBytes) {
            this.snackBar.open(`${file.name} is larger than the 25 MB upload limit.`, 'Ok');

            return;
        }

        this.selectedFile = file;
        if (!this.title.trim()) {
            this.title = this.fileTitle(file.name);
        }
    }

    public removeFile(): void
    {
        this.selectedFile = null;
    }

    public async submit(): Promise<void>
    {
        if (this.data.document) {
            await this.updateDocument();

            return;
        }

        if (!this.selectedFile) {
            this.snackBar.open('Choose a document to upload.', 'Ok');

            return;
        }

        const category = this.category;
        if (!category) {
            this.snackBar.open('Enter a category for this document.', 'Ok');

            return;
        }

        this.uploading = true;
        try {
            const checksum = await this.checksum(this.selectedFile);
            const intent = await firstValueFrom(this.documentsService.createIntent({
                documentable_type: this.data.documentableType,
                documentable_id: String(this.data.documentableId),
                category,
                title: this.title.trim() || this.fileTitle(this.selectedFile.name),
                file_name: this.selectedFile.name,
                content_type: this.selectedFile.type || 'application/octet-stream',
                size: this.selectedFile.size,
                checksum,
                ...this.visibilityChanges(),
            }));
            await firstValueFrom(this.documentsService.upload(intent.data.upload_url, this.selectedFile, this.selectedFile.type || 'application/octet-stream'));
            const finalized = await firstValueFrom(this.documentsService.finalize(intent.data.document.id, checksum));

            this.snackBar.open('Document uploaded.', 'Ok');
            this.dialogRef.close(finalized.data);
        } catch (error) {
            console.error(error);
            this.snackBar.open('There was a problem uploading the document. Please try again.', 'Ok');
        } finally {
            this.uploading = false;
        }
    }

    public get category(): string
    {
        return this.selectedCategory === 'Other' ? this.customCategory.trim() : this.selectedCategory;
    }

    public get editing(): boolean
    {
        return Boolean(this.data.document);
    }

    private async updateDocument(): Promise<void>
    {
        const category = this.category;
        if (!category) {
            this.snackBar.open('Enter a category for this document.', 'Ok');

            return;
        }

        this.uploading = true;
        try {
            const document = await firstValueFrom(this.documentsService.update(this.data.document!.id, {
                category,
                title: this.title.trim() || null,
                ...this.visibilityChanges(),
            }));
            this.snackBar.open('Document updated.', 'Ok');
            this.dialogRef.close(document.data);
        } catch (error) {
            console.error(error);
            this.snackBar.open('There was a problem updating the document. Please try again.', 'Ok');
        } finally {
            this.uploading = false;
        }
    }

    private visibilityChanges(): Partial<Pick<OperationalDocument, 'visible_to_customer' | 'visible_on_app' | 'include_in_report'>>
    {
        if (!this.canManageVisibility) {
            return {};
        }

        return {
            visible_to_customer: this.visibleToCustomer,
            visible_on_app: this.visibleOnApp,
            include_in_report: this.includeInReport,
        };
    }

    private async checksum(file: File): Promise<string>
    {
        const buffer = await file.arrayBuffer();
        const hash = await crypto.subtle.digest('SHA-256', buffer);

        return Array.from(new Uint8Array(hash)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    private fileTitle(fileName: string): string
    {
        return fileName.replace(/\.[^/.]+$/, '');
    }
}
